import type { SavedPresentation } from './model/presentation-machine'

/** Where the console keeps its running presentation across a reload. */
export type PresentationStorage = {
	read: (liveSessionId: string) => SavedPresentation | null
	write: (liveSessionId: string, saved: SavedPresentation | null) => void
}

const keyFor = (liveSessionId: string): string =>
	`beelday-presentation:${liveSessionId}`

const isSaved = (value: unknown): value is SavedPresentation => {
	if (typeof value !== 'object' || value === null) {
		return false
	}
	const { id, startedAt, blank } = value as Record<string, unknown>
	return (
		typeof id === 'string' &&
		typeof startedAt === 'number' &&
		typeof blank === 'boolean'
	)
}

/**
 * `sessionStorage`: one tab's presentation, gone with the tab. In private
 * mode it may refuse; a reload then simply ends the presentation.
 */
export const sessionPresentationStorage: PresentationStorage = {
	read: liveSessionId => {
		try {
			const raw = window.sessionStorage.getItem(keyFor(liveSessionId))
			const value: unknown = raw ? JSON.parse(raw) : null
			return isSaved(value) ? value : null
		} catch (e) {
			return null
		}
	},
	write: (liveSessionId, saved) => {
		try {
			if (saved) {
				window.sessionStorage.setItem(
					keyFor(liveSessionId),
					JSON.stringify(saved)
				)
			} else {
				window.sessionStorage.removeItem(keyFor(liveSessionId))
			}
		} catch (e) {
			// Not kept: a reload ends the presentation.
		}
	},
}
